import React, { useRef, useState } from 'react';
import {
    Row, Col,
    Button,
    Form,
    Dropdown,
    Container,
} from 'react-bootstrap';
import { Formik } from 'formik';
import RadioButtonGroup from '../Inputs/RadioButtonGroup';
import { FilterMenuProps } from './filterMenuProps';
import { DashboardItemDto, StatusEnumDto } from '../../api/web-api-client';
import { prefixedPropertyOf } from '../../utils';
import { RadioButtonProps } from '../Inputs/RadioButton/types';
import { DashboardTab } from './types';
import useAccountContext from '../../authentication/hooks';
import { defaultFilter } from '../../routes/common/constants';

const getName = prefixedPropertyOf<DashboardItemDto>('paymentDetails');

function getNameForUse2(name: string | keyof DashboardItemDto, isSummary: boolean | undefined) {
    const fullname = isSummary ? getName(name as keyof DashboardItemDto) : name;
    return fullname;
}

const FilterMenu = (props: FilterMenuProps) => {
    const {
        containerClassName = '', // default props
        className = '', // default props
        initialFilters,
        setInitialFilters,
        setCurrentPage,
    } = props;
    const accountContext = useAccountContext();
    function getNameForUse(arg0: string): string {
        return getNameForUse2(arg0, false);
    }

    const filterStatus: RadioButtonProps<string>[] = [{
        label: 'Show all statuses',
        value: 'allStatuses',
        id: 'filterStatusType-All',
    }, {
        label: 'Quote request submitted',
        value: StatusEnumDto.Submitted,
        id: 'filterStatusType-submitted',
    }, {
        label: 'Awaiting customer action',
        value: StatusEnumDto.AwaitingClientInformation,
        id: 'filterStatusType-awaiting',
    }, {
        label: 'Quote offer is available',
        value: StatusEnumDto.QuoteAvailable,
        id: 'filterStatusType-offer',
    }, {
        label: 'Report is available',
        value: StatusEnumDto.ReportIssued,
        id: 'filterStatusType-report-available',
    }];

    const filterYear: RadioButtonProps<string>[] = [{
        label: 'Show all years',
        value: 'allYears',
        id: 'filterYearType-All',
    }, {
        label: 'Current year - 2025',
        value: '2025',
        id: 'filterYearType-2025',
    }, {
        label: 'Previous year - 2024',
        value: '2024',
        id: 'filterYearType-2024',
    }];

    const [show, setShow] = useState(false);
    const closeBtnRef = useRef<HTMLButtonElement>(null);

    function handleClose(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        setShow(false);
    }

    function handleResetFilters(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        setCurrentPage(1);
        const profile = {
            filterYearType: defaultFilter.filterYearType,
            filterStatusType: defaultFilter.filterStatusType,
            filtersChanged: defaultFilter.filtersChanged,
            filterCurrentPage: 1,
            filterActiveTab: initialFilters?.filterActiveTab,
            filterSearchText: initialFilters?.filterSearchText,
        };
        setInitialFilters(profile);
        accountContext?.setUserProfile(profile);
        setShow(false);
    }

    const filterByYear = () => (
        <RadioButtonGroup
            legend='Year'
            name={getNameForUse('filterYearType')}
            id='q-filterYearType'
            isSummary={false}
            className='my-2'
            options={filterYear}
        />
    );

    return (
        <Dropdown
            align='end'
            id='search-filter-dropdown'
            data-testid='search-filter-dropdown'
            className={containerClassName}
            // onDrop={(e) => handleToggle(!show)}
            show={show}
            onToggle={(isOpen) => {
                setShow(isOpen);
                if (!isOpen) closeBtnRef.current?.click();
            }}
        >
            <Dropdown.Toggle
                id='filter-dropdown'
                aria-haspopup='true'
                aria-expanded={show}
                className='btn btn-secondary'
                title={`${initialFilters?.filtersChanged ? 'Filters have been applied' : 'No filters applied'}`}
                aria-label={`${initialFilters?.filtersChanged ? 'Filters have been applied' : 'No filters applied'}`}
            >
                <i className='icon-article ms-md-1 me-md-2' aria-hidden='true' role='presentation' />
                <span className='d-none d-md-inline-block'>
                    Filters
                    <span className='visually-hidden'>
                        {' Results'}
                    </span>
                </span>
                {/* Show icon dot if non-default filters have been applied */}
                <i
                    className={`icon-dot me-md-2 fade ${initialFilters?.filtersChanged ? 'show text-danger' : ''}`}
                    role='presentation'
                    aria-hidden='true'
                />
            </Dropdown.Toggle>
            <Dropdown.Menu flip={false} className={`filter-menu ${containerClassName} shadow`} aria-label='Filter Menu'>
                <Formik
                    enableReinitialize
                    initialValues={initialFilters ?? defaultFilter}
                    onSubmit={async (values, { setSubmitting, setValues }) => {
                        const { filterYearType, filterStatusType, filterSortOrder } = values;
                        const filtersChanged = filterStatusType !== defaultFilter.filterStatusType || filterYearType !== defaultFilter.filterYearType;
                        // reset page to 1 this is common practice when changing filters
                        setCurrentPage(1);
                        // setInitialfilters will trigger useEffect and an API call
                        const filters = {
                            filterYearType,
                            filterStatusType,
                            filtersChanged,
                            filterSortOrder,
                            filterCurrentPage: 1,
                            filterActiveTab: initialFilters?.filterActiveTab,
                            filterSearchText: initialFilters?.filterSearchText,
                        };
                        setInitialFilters(filters);
                        // setValues will set the initialValues to the current search values
                        setValues({
                            filterYearType, filterStatusType, filtersChanged, filterSortOrder,
                        });
                        // save user profile
                        accountContext?.setUserProfile(filters);
                        setSubmitting(false);
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                >
                    {({
                        submitForm, handleChange, resetForm, setValues,
                    }) => (
                        <Form>
                            <Container>
                                <Row className='mb-3 g-0'>
                                    <Col xs={10} md={11}>
                                        <Row>
                                            <h3 className='visually-hidden'>Select your dashboard filter options</h3>
                                            <Col xs={12} sm={6} className='pt-2'>
                                                <RadioButtonGroup
                                                    legend='Status'
                                                    name={getNameForUse('filterStatusType')}
                                                    isSummary={false}
                                                    id='q-filterStatusType'
                                                    options={filterStatus}
                                                    onChange={(e) => handleChange(e)}
                                                    disabled={initialFilters?.filterActiveTab === DashboardTab.Drafts
                                                        || initialFilters?.filterActiveTab === DashboardTab.Instruments}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} className='pt-2'>
                                                {filterByYear()}
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={2} md={1} className='text-end'>
                                        <Button
                                            ref={closeBtnRef}
                                            data-testid='close-filter-button'
                                            onClick={(e) => {
                                                handleClose(e);
                                                resetForm({ values: initialFilters });
                                            }}
                                            variant='tertiary'
                                            className='ms-md-auto'
                                        >
                                            <i className='icon-close me-1' aria-hidden='true' />
                                            <span className='visually-hidden'>Close filter menu</span>
                                        </Button>
                                    </Col>
                                </Row>
                                <Row className='mb-3'>
                                    <Col>
                                        <div className='d-grid w-100 gap-3 d-md-flex justify-content-md-between'>
                                            <Button
                                                data-testid='cancel-filter-button'
                                                onClick={(e) => {
                                                    handleClose(e);
                                                    resetForm({ values: initialFilters });
                                                }}
                                                variant='tertiary'
                                                className='me-md-auto -mb-4 order-2 order-md-0'
                                            >
                                                <i className='icon-close me-1' aria-hidden='true' />
                                                Cancel
                                            </Button>
                                            <div className='d-grid gap-4 d-md-flex'>
                                                <Button
                                                    data-testid='reset-filter-button'
                                                    onClick={(e) => {
                                                        handleResetFilters(e);
                                                        resetForm({ values: defaultFilter });
                                                    }}
                                                    variant='secondary'
                                                    className='me-md-auto order-2 order-md-0'
                                                >
                                                    Reset
                                                </Button>
                                                <Button
                                                    data-testid='apply-filter-button'
                                                    onClick={(e) => {
                                                        submitForm();
                                                        handleClose(e);
                                                    }}
                                                    variant='primary'
                                                    className='ms-md-auto'
                                                >
                                                    Apply
                                                    <span className='visually-hidden'>{' filters to results'}</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Form>
                    )}
                </Formik>
            </Dropdown.Menu>
        </Dropdown>

    );
};

export default FilterMenu;
